import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AvailableChapels } from "../../types/chapel";
import { RootState } from "../../app/store";
import { ClientData, DateRange } from "../../types/reports";
import { initialDateRangeReport } from "../../initial-state/initial-state";

type BillingReportType = "summary" | "detailed";

interface ReportsState {
    selectedChapelReport: AvailableChapels[] 
    selectedClientReport: ClientData | null
    dateRangeReport: DateRange;
    selectedBillingReportTypes: BillingReportType[];
    selectedDeceasedName: string | null;
    selectedClientIdBillingReport: number | null;
}

const initialState: ReportsState = {
    selectedChapelReport: [],
    selectedClientReport: null,
    dateRangeReport: initialDateRangeReport,
    selectedBillingReportTypes: ["summary", "detailed"],
    selectedDeceasedName: null,
    selectedClientIdBillingReport: null,
}

const reportsSlice = createSlice({
    name: 'reports',
    initialState,
    reducers: {
        setSelectedClientIdBillingReport: (state, action: PayloadAction<number | null>) => {
            state.selectedClientIdBillingReport = action.payload;
        },
        setSelectedDeceaseName: (state, action: PayloadAction<string | null>) => {
            state.selectedDeceasedName = action.payload;
        },
        setSelectedBillingReportTypes: ( state, action: PayloadAction<BillingReportType[]>) => {
            state.selectedBillingReportTypes = action.payload;
        },
        toggleBillingReportType: (state, action: PayloadAction<BillingReportType>) => {
            const type = action.payload;
            if (state.selectedBillingReportTypes.includes(type)) {
                state.selectedBillingReportTypes =
                    state.selectedBillingReportTypes.filter(
                        (t) => t !== type
                    );
            } else {
                state.selectedBillingReportTypes.push(type);
            }
        },
        setDateRangeReport: (state, action: PayloadAction<{type: 'from' | 'to', value: string}>) => {
            const { type, value } = action.payload
            if (type === "from") {
                state.dateRangeReport.from = value;
            } else {
                state.dateRangeReport.to = value;
            }
        },
        clearDateRangeReport: (state) => {
            state.dateRangeReport = initialDateRangeReport
        },

        setSelectedClientReport: (state, action: PayloadAction<ClientData | null>) => {
            state.selectedClientReport = action.payload;
        },
        clearSelectedClientReport: (state) => {
            state.selectedClientReport = null;
        },

        setSelectedChapelReport: (state, action: PayloadAction<AvailableChapels | AvailableChapels[]>) => {
            if (Array.isArray(action.payload)) {
                // Replace the entire array
                state.selectedChapelReport = action.payload;
                return;
            }

            const chapel = action.payload;
            const index = state.selectedChapelReport.findIndex(
                (item) => item.chapelID === chapel.chapelID
            );

            if (index >= 0) {
                // Already selected → remove it
                state.selectedChapelReport.splice(index, 1);
            } else {
                // Not selected → add it
                state.selectedChapelReport.push(chapel);
            }
        },
        clearSelectedChapelReport: (state) => {
            state.selectedChapelReport = [];
        },
        clearReportsState: () => initialState,
    },
})

export const {
    setSelectedClientIdBillingReport,

    setSelectedDeceaseName,
    
    setSelectedBillingReportTypes,
    toggleBillingReportType,

    setDateRangeReport,
    clearDateRangeReport,

    setSelectedClientReport,
    clearSelectedClientReport,

    setSelectedChapelReport,
    clearSelectedChapelReport,
    clearReportsState
} = reportsSlice.actions;

export default reportsSlice.reducer;

export const selectSelectedChapelReport = (state: RootState) => state.reports.selectedChapelReport;
export const selectSelectedClientReport = (state: RootState) => state.reports.selectedClientReport;
export const selectSelectedClientIdReport = (state: RootState) => state.reports.selectedClientReport?.clientID;
export const selectDateRangeReport = (state: RootState) => state.reports.dateRangeReport;
export const selectTypeOfBillingReport = (state: RootState) => state.reports.selectedBillingReportTypes;
export const selectDeceaseName = (state: RootState) => state.reports.selectedDeceasedName;
export const selectClientIdBillingReport = (state: RootState) => state.reports.selectedClientIdBillingReport;

